import { Doc, docCurrency, docTotal, statusGroup } from '@/components/documents/documentUtils'
import { useCountries } from '@/hooks/useCountries'
import { Participant } from '@/interfaces/Participant'

export interface Balance {
  /** Neuhrazeno podle měny (v haléřích) */
  open: Record<string, number>
  /** Z toho po splatnosti podle měny */
  overdue: Record<string, number>
  /** Fakturováno celkem (bez zrušených) podle měny */
  billed: Record<string, number>
  openCount: number
  overdueCount: number
}

const emptyBalance = (): Balance => ({ open: {}, overdue: {}, billed: {}, openCount: 0, overdueCount: 0 })

const add = (acc: Record<string, number>, currency: string, amount: number) => {
  acc[currency] = (acc[currency] ?? 0) + amount
}

export const balanceOf = (docs: Doc[] | undefined): Balance => {
  const b = emptyBalance()
  for (const d of docs ?? []) {
    const group = statusGroup(d)
    if (group === 'cancelled') continue
    const currency = docCurrency(d)
    const total = docTotal(d)
    add(b.billed, currency, total)
    if (group === 'open' || group === 'overdue') {
      add(b.open, currency, total)
      b.openCount += 1
    }
    if (group === 'overdue') {
      add(b.overdue, currency, total)
      b.overdueCount += 1
    }
  }
  return b
}

/** Zůstatky podle odběratele (klíč = id kontaktu) z jednoho seznamu dokladů. */
export const balancesByCounterparty = (docs: Doc[] | undefined, side: 'customer' | 'supplier') => {
  const groups: Record<string, Doc[]> = {}
  for (const d of docs ?? []) {
    const id = d[side]?.id
    if (!id) continue
    ;(groups[id] ??= []).push(d)
  }
  const result: Record<string, Balance> = {}
  for (const [id, list] of Object.entries(groups)) result[id] = balanceOf(list)
  return result
}

/** Částky seřazené tak, aby hlavní měna byla první. */
export const currencyEntries = (amounts: Record<string, number>, primary: string) =>
  Object.entries(amounts)
    .filter(([, v]) => v !== 0)
    .sort(([a], [b]) => (a === primary ? -1 : b === primary ? 1 : a.localeCompare(b)))

export const useCountryName = () => {
  const countries = useCountries()
  return (code: string | null | undefined) => (code ? (countries.find((c) => c.code === code)?.name ?? code) : null)
}

export const formatAccount = (p: Pick<Participant, 'accountNumber' | 'iban'>) => p.accountNumber || p.iban || null
